import React, { useState, useEffect } from 'react';
import InputText from '@Common/InputText';
import Toggle from '@Common/Toggle/Toggle';
import Button from '@Common/Button';
import Select from 'react-select';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import Notification from '@Common/Notification';
import Drawer from '@Common/Drawer/Drawer';
import { DATE_FORMAT } from '@Constants/constants';
import Moment from 'moment';
import { useGetBaseline, useLoadBaselineFilters, useSaveBaseline } from '@Apis/baselines';
import { Baseline, BaselineUser } from '@Apis/baselines/baseline.types';

interface BaselinesFormProps {
    subscriptionId: string;
    cancelClick: () => void;
    saveClick: () => void;
    baselineId?: number;
    projectKey?: string;
}

const emptyBaseline: Baseline = {
    id: 0,
    subscriptionId: 0,
    name: '',
    description: '',
    isActive: true,
    jobTitles: [],
    users: [],
    userIds: [],
};

const BaselinesForm: React.FC<BaselinesFormProps> = ({ subscriptionId, cancelClick, saveClick, baselineId, projectKey }) => {
    const isEditing = !!baselineId && baselineId > 0;

    const [baseline, setBaseline] = useState<Baseline>(emptyBaseline);
    const [availableUsers, setAvailableUsers] = useState<BaselineUser[]>([]);
    const [selectedNewUser, setSelectedNewUser] = useState<BaselineUser | null>(null);
    const [selectedExistingUserIds, setSelectedExistingUserIds] = useState<string[]>([]);
    const [isFormValid, setIsFormValid] = useState(true);

    // Always call the hook, but control execution with 'enabled'
    const { data: fetchedBaseline, isFetching: isFetchingBaseline } = useGetBaseline(baselineId ?? 0, { enabled: !!baselineId && baselineId > 0 });

    const { data: filtersData, isFetching: isFetchingFilters } = useLoadBaselineFilters({ subscriptionId: Number(subscriptionId) });

    // ---- Mutations ----
    const { mutate: saveBaseline, isPending: isSaving } = useSaveBaseline((result) => {
        Notification('success', 'Save Successful', 'Baseline saved successfully');
        saveClick();
    });

    // ---- Effects ----

    // Populate baseline when fetched (edit mode)
    useEffect(() => {
        if (fetchedBaseline) {
            setBaseline(fetchedBaseline);
        }
    }, [fetchedBaseline]);

    // Populate available users from filters, excluding already-assigned users
    useEffect(() => {
        if (!filtersData || isEditing) return;
        const { users, usersInModeratorRole } = filtersData;
        // When creating, pre-select all non-moderator users
        const nonModeratorUsers = users.filter((u) => !usersInModeratorRole.some((mod) => mod.id === u.id));
        setBaseline((prev) => ({ ...prev, users: nonModeratorUsers }));
        setAvailableUsers(users.filter((u) => !nonModeratorUsers.some((nm) => nm.id === u.id)));
    }, [filtersData, isEditing]);
    // Edit mode: recompute available users when baseline users or filters change
    useEffect(() => {
        if (!filtersData || !isEditing) return;
        const { users } = filtersData;
        // In edit mode, available = all users not already on the baseline
        setAvailableUsers(users.filter((u) => !baseline.users.some((bu) => bu.id === u.id)));
    }, [filtersData, isEditing, baseline.users]);

    const isLoading = isFetchingBaseline || isFetchingFilters || isSaving;

    // ---- Handlers ----

    const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setBaseline((prev) => ({ ...prev, [name]: value }));
    };

    const onToggleChange = (name: string, value: boolean) => {
        setBaseline((prev) => ({ ...prev, [name]: value }));
    };

    const addNewUser = () => {
        if (!selectedNewUser) return;

        const userExists = baseline.users.some((u) => u.id === selectedNewUser.id);
        if (userExists) return;

        setBaseline((prev) => ({ ...prev, users: [...prev.users, selectedNewUser] }));
        setAvailableUsers((prev) => prev.filter((u) => u.id !== selectedNewUser.id));
        setSelectedNewUser(null);
    };

    const removeSelectedExistingUsers = () => {
        if (!selectedExistingUserIds.length) return;

        const idsToRemove = selectedExistingUserIds.map(Number);
        const removedUsers = baseline.users.filter((u) => idsToRemove.includes(u.id));

        setBaseline((prev) => ({
            ...prev,
            users: prev.users.filter((u) => !idsToRemove.includes(u.id)),
        }));
        setAvailableUsers((prev) => [...prev, ...removedUsers]);
        setSelectedExistingUserIds([]);
    };

    const selectExistingUser = (e: React.MouseEvent<HTMLSelectElement>) => {
        const options = e.currentTarget.options;
        const ids: string[] = [];
        for (let i = 0; i < options.length; i++) {
            if (options[i].selected) ids.push(options[i].value);
        }
        setSelectedExistingUserIds(ids);
    };

    const validateForm = (): boolean => {
        if (!baseline.name.length) return false;
        if (!baseline.users.length) {
            Notification('error', 'At least one user is required', '');
            return false;
        }
        return true;
    };

    const onSaveClick = () => {
        if (!validateForm()) {
            setIsFormValid(false);
            return;
        }

        saveBaseline({
            id: baseline.id,
            name: baseline.name,
            description: baseline.description,
            isActive: baseline.isActive,
            subscriptionId: Number(subscriptionId),
            userIds: baseline.users.map((u) => u.id),
            jobTitles: baseline.jobTitles,
            users: baseline.users,
            projectKey: projectKey ?? null,
        }, {
            onError: (error: Error) => Notification('error', 'Save Failed', error.message ?? 'An unexpected error occurred'),
        });
    };

    // Deduplicate users before rendering, and notify once if duplicates are detected
    const renderCurrentUserList = () => {
        const seenIds = new Set<number>();
        const dedupedUsers: BaselineUser[] = [];
        let hasDuplicates = false;
        for (const u of baseline.users) {
            if (!seenIds.has(u.id)) {
                seenIds.add(u.id);
                dedupedUsers.push(u);
            } else {
                hasDuplicates = true;
            }
        }

        if (hasDuplicates) {
            Notification('error', 'Error', 'User already added');
        }

        return dedupedUsers.map((u) => (
            <option key={u.id} value={u.id}>
                {u.name}
            </option>
        ));
    };

    // ---- Render ----
    return (
        <Drawer isWide={false} title='Baseline' onSaveClick={onSaveClick} onCancelClick={cancelClick}>
            <LoadingIndicator isLoading={isLoading} hideWhileLoading isFullscreen>
                <div className='identifier-form'>
                    <form noValidate className={isFormValid ? '' : 'was-validated'}>
                        <InputText
                            name='name'
                            label='Name'
                            placeholder='Name'
                            type='text'
                            value={baseline.name}
                            onChange={onTextChange}
                            isRequired
                            errorMessage='Name is required'
                        />
                        <InputText
                            name='description'
                            label='Description'
                            placeholder='Description'
                            type='text'
                            value={baseline.description ?? ''}
                            onChange={onTextChange}
                            isRequired={false}
                        />
                        <Toggle name='isActive' text='Active' checked={baseline.isActive} onChange={onToggleChange} />
                        <input type='submit' className='d-none' />
                    </form>

                    <div className='d-flex justify-content-between align-items-center'>
                        <div className='flex-grow-1 me-2'>
                            <Select
                                name='baseline-users'
                                options={availableUsers}
                                getOptionLabel={(opt) => opt.name}
                                getOptionValue={(opt) => String(opt.id)}
                                onChange={(val) => setSelectedNewUser(val)}
                                value={selectedNewUser}
                                className='w-100'
                                placeholder='Search for User to Add...'
                                isDisabled={availableUsers.length === 0}
                            />
                        </div>
                        <button className='btn' onClick={addNewUser}>
                            <i className='fas fa-plus-circle fa-lg' />
                        </button>
                    </div>

                    <span className='has-float-label'>
                        <select id='baselineUserList' className='form-control mt-3' multiple size={8} onClick={selectExistingUser}>
                            {renderCurrentUserList()}
                        </select>
                        <label htmlFor='baselineUserList'>Current Users</label>
                    </span>

                    <div className='text-end'>
                        <Button
                            type='button'
                            bootstrapClass='btn btn-outline-secondary mt-2 mb-2'
                            name='btnRemoveUser'
                            text='Remove Selected'
                            onClick={removeSelectedExistingUsers}
                        />
                    </div>

                    <div>Last Location Task Date: {baseline.latestTaskDate ? Moment.utc(baseline.latestTaskDate).format(DATE_FORMAT) : 'none'}</div>
                </div>
            </LoadingIndicator>
        </Drawer>
    );
};

export default BaselinesForm;
